import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  putMerchantsPublicIDEmployeesEmployeePublicID,
  type EmployeeDto,
  type ApiProblemDetails,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { merchantEmployeesKeys } from "Api/Queries/Merchants/Employees/useMerchantEmployees";
import { merchantEmployeeKeys } from "Api/Queries/Merchants/Employees/useMerchantEmployee";

export type UpdateEmployeeParameters = {
  data: Parameters<typeof putMerchantsPublicIDEmployeesEmployeePublicID>[0];
  publicID: string;
  employeePublicID: string;
};

export type Props = {
  onSuccess?: (employee: EmployeeDto) => void;
  onError?: (error: ApiProblemDetails) => void;
};

export const useUpdateEmployeeMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ data, publicID, employeePublicID }: UpdateEmployeeParameters) =>
        putMerchantsPublicIDEmployeesEmployeePublicID(
          data,
          publicID,
          employeePublicID,
        ),
    ),
    onSuccess: (
      data: EmployeeDto,
      { publicID, employeePublicID }: UpdateEmployeeParameters,
    ) => {
      // The list page queries without a merchant id, so invalidate every list, not list(publicID).
      queryClient.invalidateQueries({
        queryKey: merchantEmployeesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: merchantEmployeeKeys.singleEmployee(
          publicID,
          employeePublicID,
        ),
      });
      onSuccess?.(data);
    },
    onError,
  });
};
